import { HeartIcon as OutlinedHeartIcon } from '@heroicons/react/outline';
import { HeartIcon as SolidHeartIcon } from '@heroicons/react/solid';
import { ConfirmDialog, useToast } from '@koolumbus/web/ui';
import { createTxs, useToggle } from '@koolumbus/web/utils';
import clsx from 'clsx';
import React, { useMemo } from 'react';
import { useAuthUser } from '../../lib/context/auth.context';
import { trpc } from '../../lib/trpc';
import AuthModal from './auth-modal/AuthModal';

interface FavoriteButtonProps {
  propertyId: string;
  size?: 'sm' | 'md';
}

const FavoriteButton: React.FC<React.PropsWithChildren<FavoriteButtonProps>> = ({
  propertyId,
  size = 'md',
}) => {
  const user = useAuthUser();
  const trpcCtx = trpc.useUtils();
  const toast = useToast();
  const tx = useTxs();

  const isSmall = size === 'sm';

  const [isAuthModalOpen, { off: closeAuthModal, on: openAuthModal }] = useToggle(false);
  const [isConfirmOpen, { off: closeConfirm, on: openConfirm }] = useToggle(false);

  const isFavorite = useMemo(() => {
    if (user) {
      for (const fav of user.favorites) {
        if (fav.propertyId === propertyId) {
          return true;
        }
      }
    }

    return false;
  }, [propertyId, user]);

  const { isLoading, mutate } = trpc.favorites.toggle.useMutation({
    onSuccess: () => {
      trpcCtx.auth.me.invalidate();
      trpcCtx.favorites.list.invalidate();

      if (isFavorite) {
        toast.warning(tx.remove.success);
      } else {
        toast.success(tx.save.success);
      }
    },
    onError: (err) => {
      console.error(err);
      toast.error(tx.error);
    },
  });

  const performToggle = () => mutate({ propertyId });

  const onToggleFavorite = async () => {
    if (isLoading) return;

    if (isFavorite) {
      openConfirm();
    } else {
      performToggle();
    }
  };

  const onClick = () => {
    if (!user) {
      openAuthModal();
      return;
    }

    onToggleFavorite();
  };

  const iconClass = clsx('text-red-400', isSmall ? 'w-5 h-5' : 'w-6 h-6');

  return (
    <>
      <button
        onClick={onClick}
        aria-label="toggle favorite"
        type="button"
        className={clsx(
          'inline-flex justify-center items-center border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-400',
          isSmall ? 'w-9 h-9' : 'w-10 h-10',
        )}
      >
        {isFavorite ? (
          <SolidHeartIcon className={iconClass} aria-hidden={true} />
        ) : (
          <OutlinedHeartIcon className={iconClass} aria-hidden={true} />
        )}
      </button>

      <ConfirmDialog
        variant="warning"
        title={tx.remove.title}
        body={tx.remove.body}
        isOpen={isConfirmOpen}
        onClose={closeConfirm}
        onConfirm={performToggle}
      />

      <AuthModal isOpen={isAuthModalOpen} onClose={closeAuthModal} onLoggedIn={onToggleFavorite} />
    </>
  );
};

const useTxs = createTxs({
  en: {
    save: {
      success: 'Saved as favorite',
    },
    remove: {
      title: 'Remove from favorites',
      body: 'Do you really want to remove this property from your favorites?',
      success: 'Removed from favorites',
    },
    error: 'Something went wrong, please try again later',
  },
  it: {
    save: {
      success: 'Aggiunto ai preferiti',
    },
    remove: {
      title: 'Rimuovi dai preferiti',
      body: 'Vuoi davvero rimuovere questa proprietà dai tuoi preferiti?',
      success: 'Rimosso con successo dai preferiti',
    },
    error: 'Qualcosa è andato storto, per favore riprova più tardi',
  },
});

export default FavoriteButton;
